import { HttpClient } from '@angular/common/http';
import { computed, inject, Inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { COMMON_ENTITY_CONFIG, EntityServiceConfig, HttpGetParams, StorageService, UiService, UserBaseService } from 'ui-ionic-comp';
import { OrganizationUserRoles, OrganizationUsersResponse } from '../organization-user/organization-user.entity';
import { OrganizationService } from '../organizations/organization.service';

@Injectable({
  providedIn: 'root'
})
export class UserService extends UserBaseService {

  // Services
  private organizationService = inject(OrganizationService);
  private storageService = inject(StorageService);

  // Signals
  private readonly userRoles = signal<OrganizationUsersResponse | undefined>(undefined);

  constructor(
    @Inject(COMMON_ENTITY_CONFIG) private __config: EntityServiceConfig,
    private __httpClient: HttpClient,
    private __uiService: UiService) {

    super(__config, __httpClient, __uiService);

    this.apiDir = 'users';
  }

  readonly isUserAdminInOrganization = computed(() => {
    const userRoles = this.userRoles();
    const organization = this.organizationService.organization();

    const isAdmin = userRoles?.contents.some(x =>
      x.role?.key === OrganizationUserRoles.Admin &&
      x.orgaId === organization?.id);

    return isAdmin;
  });

  getUserRoles$(userId: string): Observable<OrganizationUsersResponse> {
    const params: HttpGetParams = {
      endpoint: `${userId}/roles`,
    };

    return this.getEntities$(params) as Observable<OrganizationUsersResponse>;
  }

  // Ensures that the user roles are loaded from storage when the page is refreshed
  async loadUserRolesFromStorage(): Promise<void> {
    const userRolesResult = await this.storageService.getStorage('userRoles');

    const organizationUsersResponse: OrganizationUsersResponse = userRolesResult?.value ? JSON.parse(userRolesResult.value) : undefined;

    this.userRoles.set(organizationUsersResponse);
  }

  async persistUserRoles(roles: OrganizationUsersResponse): Promise<void> {
    this.setUserRoles(roles);

    await this.storageService.setStorage('userRoles', JSON.stringify(roles));
  }

  private setUserRoles(organizationUsersResponse: OrganizationUsersResponse) {
    this.userRoles.set(organizationUsersResponse);
  }

  clearUserRoles(): void {
    this.userRoles.set(undefined);

    this.storageService.removeStorage('userRoles');
  }
}
